import type { DepotFormValues } from '../types'

import { useCallback } from 'react'

import { setCrudSelection, useListApi, useResetEditingState } from '@/atoms'
import { createDepot, updateDepot } from '@/features/domain/depot'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { emptyDepotId } from '../utils/createEmptyDepot'
import { parseDepotFromFormValues } from '../utils/parseDepotFromFormValues'

export const useOnSubmit = () => {
  const stopEditing = useResetEditingState()
  const { scrollTo } = useListApi('depots')
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const onCreate = useCallback(
    async (formValues: DepotFormValues) => {
      try {
        const depot = parseDepotFromFormValues(formValues)
        const thunkResult = await dispatch(createDepot(depot))

        if (!createDepot.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        stopEditing()
        setCrudSelection('depots', thunkResult.payload)

        scrollTo(thunkResult.payload[0])
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, scrollTo, toast],
  )

  const onUpdate = useCallback(
    async (formValues: DepotFormValues) => {
      try {
        const depot = parseDepotFromFormValues(formValues)
        const thunkResult = await dispatch(updateDepot(depot))

        if (!updateDepot.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast],
  )

  return useCallback(
    (formState: DepotFormValues) =>
      formState.id === emptyDepotId ? onCreate(formState) : onUpdate(formState),
    [onCreate, onUpdate],
  )
}
